import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Stack } from '@mantine/core'
import { useNavigate } from 'react-router'
import MainTable from 'app/views/components/Table/MainTable'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { ServicingParams } from 'app/models/servicing-params'
import {
  getServicingLoader,
  getServicingRows,
} from 'app/state/ducks/servicing/selectors'
import { formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'
import {
  GridColumnVisibilityStorage,
  GridPaginationStorage,
  GridSortingStorage,
  useGridColumnVisibility,
  useGridPagination,
  useGridSorting,
} from 'app/utils/grid-sorting-storage'
import { ServicingCols } from '../servicing/servicing-columnDefs'
import { handleServicingRowClicked } from '../servicing/servicing-navigation'
import { useServicingFeed } from '../servicing/use-servicing-feed'
import { myworkKPIData } from './mywork-kpi-data'
import { calcTotalAmount } from './mywork-util/my-work-utils'
import {
  MYWORK_SCHEDULED_TYPES,
  filterMyWorkRows,
  myWorkFeedUpperBound,
} from './mywork-util/servicing-feed-window'

interface props {
  /** False until the business date has settled: the feed is not read on the today-fallback (LOS-7219). */
  dateReady?: boolean
  date: Date
  useDateRange?: boolean
  dateRange?: [Date, Date]
}

/**
 * My Work → Scheduled: the servicing feed (GET /api/servicing/events) with
 * the tab's look-ahead as `to`; the tab's type partition and the date
 * post-filter are the page's own, unchanged. The 13 list loads are gone.
 */
export function ScheduledPage({
  date,
  useDateRange = false,
  dateRange,
  dateReady = true,
}: props) {
  const navigate = useNavigate()
  const rows = useSelector(getServicingRows)
  const isLoading = useSelector(getServicingLoader)

  const to = myWorkFeedUpperBound(date, useDateRange, dateRange)
  useServicingFeed({ to }, { enabled: dateReady })

  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.myworkScheduled,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.myworkScheduled,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.myworkScheduled)

  const scheduleTableFiltered = useMemo(
    () =>
      filterMyWorkRows(
        rows.filter(row => MYWORK_SCHEDULED_TYPES.includes(row.type)),
        date,
        useDateRange,
        dateRange,
      ),
    [rows, date, useDateRange, dateRange],
  )

  const deadline =
    stringToDate(
      scheduleTableFiltered[0]?.effectiveDate.toString(),
    ).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }) ?? 'N/A'
  const totalAmount =
    formatNumberToCurrency(
      calcTotalAmount(
        // a fee still being priced, or that could not be, has no amount to add
        scheduleTableFiltered
          .filter(item => !item?.amountPending)
          .map(item => item?.amount ?? 0),
      ) ?? 0,
      'USD',
    )?.toString() ?? 'N/A'

  const kpiData = myworkKPIData({
    activityCount: scheduleTableFiltered.length,
    percentageComplete: 'N/A',
    deadlineApproaching: deadline === 'Invalid Date' ? 'N/A' : deadline,
    totalActivityAmount: totalAmount,
  })

  return (
    <Stack w="100%">
      <KPI_Section kpiData={kpiData} isLoading={isLoading} />
      <MainTable
        tableName="Scheduled Table"
        isLoading={isLoading}
        withPagination={true}
        csvExportEnabled={true}
        enableTopToolbar={false}
        headerBackgroundColor="#F0EEEE"
        overflow="hidden"
        minHeight="0px"
        columnDefs={ServicingCols}
        data={scheduleTableFiltered}
        initialSorting={[
          { id: sorting.sortBy, desc: sorting.order === 'desc' },
        ]}
        onSortingChange={handleSortingChange}
        initialPagination={pagination}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        setRow={(e: ServicingParams, cellName: string) => {
          if (cellName != 'id') handleServicingRowClicked(e, navigate)
        }}
      />
    </Stack>
  )
}
